import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';
import { UseFormResetField } from 'react-hook-form/dist/types/form';
import { compareDesc } from 'date-fns';

import { normalizeToUTCMidnight } from '@utils/kpi-utils/date';

import { IFlatMoTreeNode } from '@interfaces/components/tree';
import { IPresetDTO } from '@interfaces/kpi/dto/kpiDTO';
import { TResetSitesAndVendorArgs } from '@interfaces/kpi/kpiCommon';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EFormFields } from '@enums/kpi-api';
import { EGranularityValue } from '@enums/kpi-common';

import { dateOffsetOptions, granularitiesList } from '@components/Kpi/Parameters/constants';

interface UsePresetLogicProps {
	formEditablePreset?: IPresetDTO;
	minDate: Date;
	kpiGroupData: IFlatMoTreeNode[];
	countersGroupData: IFlatMoTreeNode[];
	treeKpi: IFlatMoTreeNode[];
	treeCounters: IFlatMoTreeNode[];
	setValue: UseFormSetValue<FieldValues>;
	resetField: UseFormResetField<FieldValues>;
	resetKpi: () => void;
	handleUpdateTreeWithWorker: (tree: IFlatMoTreeNode[], kpiIds: string[]) => Promise<IFlatMoTreeNode[]>;
	setTreeKpi: Dispatch<SetStateAction<IFlatMoTreeNode[]>>;
	setTreeCounters: Dispatch<SetStateAction<IFlatMoTreeNode[]>>;
	setTreeKpiIds: (ids: string[]) => void;
	setTreeCountersIds: (ids: string[]) => void;
	setInitKpiData: (nodes: IFlatMoTreeNode[]) => void;
	setInitCountersData: (nodes: IFlatMoTreeNode[]) => void;
	setIsDirtyPresetForm: Dispatch<SetStateAction<boolean>>;
	resetSitesAndVendor: (...params: TResetSitesAndVendorArgs) => void;
	defaultFrom: number;
	defaultTo: number;
	setIsLoadingSetPreset: Dispatch<SetStateAction<boolean>>;
	dynamicDatesOptions: TSelectOptionType<string>[];
}

export const usePresetLogic = ({
	formEditablePreset,
	minDate,
	kpiGroupData,
	countersGroupData,
	treeKpi,
	treeCounters,
	setValue,
	resetField,
	resetKpi,
	handleUpdateTreeWithWorker,
	setTreeKpi,
	setTreeCounters,
	setTreeKpiIds,
	setTreeCountersIds,
	setInitKpiData,
	setInitCountersData,
	setIsDirtyPresetForm,
	resetSitesAndVendor,
	defaultFrom,
	defaultTo,
	setIsLoadingSetPreset,
	dynamicDatesOptions,
}: UsePresetLogicProps) => {
	const setDefaultDates = useCallback(() => {
		setValue(EFormFields.from, defaultFrom);
		setValue(EFormFields.to, defaultTo);
	}, [defaultFrom, defaultTo, setValue]);

	const availableKpiIds = useMemo(() => {
		return new Set(
			kpiGroupData.map(item => item.dbId)
				.filter(id => id),
		);
	}, [kpiGroupData]);

	const availableCountersIds = useMemo(() => {
		return new Set(
			countersGroupData.map(item => item.dbId)
				.filter(id => id),
		);

	}, [countersGroupData]);

	const setSelectedPreset = useCallback(async () => {
		if (!formEditablePreset) return;

		if (formEditablePreset?.vendor) {
			resetSitesAndVendor(formEditablePreset, false);
		}

		if (formEditablePreset.aggregationType) {
			setValue(EFormFields.aggregationType, formEditablePreset.aggregationType);
		} else {
			resetField(EFormFields.aggregationType);
		}

		if (formEditablePreset.from) {
			if (compareDesc(formEditablePreset.from, minDate) > 0) {
				setDefaultDates();
			} else {
				setValue(EFormFields.from, normalizeToUTCMidnight(formEditablePreset.from));
			}
		} else {
			setValue(EFormFields.from, null);
		}

		if (formEditablePreset.to) {
			if (compareDesc(formEditablePreset.to, minDate) > 0) {
				setDefaultDates();
			} else {
				setValue(EFormFields.to, normalizeToUTCMidnight(formEditablePreset.to));
			}
		} else {
			setValue(EFormFields.to, null);
		}

		if (formEditablePreset.dateSelectionMode) {
			setValue(EFormFields.dateSelectionMode, formEditablePreset.dateSelectionMode);
		}

		if (formEditablePreset.dynamicDateRange) {
			const preparedDynamicDateRange: TSelectOptionType<string> | undefined = dynamicDatesOptions.find(option => {
					return option?.value === formEditablePreset.dynamicDateRange;
				},
			);

			if (preparedDynamicDateRange) {
				setValue(EFormFields.dynamicDateRange, preparedDynamicDateRange);
			}
		} else {
			resetField(EFormFields.dynamicDateRange);
		}

		if (formEditablePreset.startDateOffset !== undefined) {
			const preparedStartDateOffset: TSelectOptionType<number> | undefined = dateOffsetOptions.find(option => {
					return option?.value === formEditablePreset.startDateOffset;
				},
			);

			if (preparedStartDateOffset) {
				setValue(EFormFields.startDateOffset, preparedStartDateOffset);
			}
		} else {
			resetField(EFormFields.startDateOffset);
		}

		if (formEditablePreset.isAutomaticGranularity) {
			setValue(EFormFields.isAutomaticGranularity, formEditablePreset.isAutomaticGranularity);
			setValue(EFormFields.granularity, null);
		} else if (formEditablePreset.granularity && formEditablePreset.isAutomaticGranularity !== undefined) {
			const preparedGranularity: TSelectOptionType<EGranularityValue> | undefined = granularitiesList.find(
				granularity => granularity.value === formEditablePreset.granularity,
			);

			if (preparedGranularity) {
				setValue(EFormFields.isAutomaticGranularity, false);
				setValue(EFormFields.granularity, preparedGranularity);
			}
		}

		if (formEditablePreset.kpiIds) {
			setIsLoadingSetPreset(true);

			const idsForKpi = formEditablePreset.kpiIds.filter((id) => availableKpiIds.has(id));
			const idsForCounters = formEditablePreset.kpiIds.filter((id) => availableCountersIds.has(id));

			try {
				if (idsForKpi.length) {
					const updatedTreeKpi = await handleUpdateTreeWithWorker(treeKpi, idsForKpi);

					setTreeKpi(updatedTreeKpi);
					setInitKpiData(updatedTreeKpi);
					setTreeKpiIds(idsForKpi);
				} else {
					const updatedTreeKpi = await handleUpdateTreeWithWorker(treeKpi, idsForKpi);

					setTreeKpi(updatedTreeKpi);
					setInitKpiData(updatedTreeKpi);
					setTreeKpiIds([]);
				}

				if (idsForCounters.length) {
					const updatedTreeCounters = await handleUpdateTreeWithWorker(treeCounters, idsForCounters);

					setTreeCounters(updatedTreeCounters);
					setInitCountersData(updatedTreeCounters);
					setTreeCountersIds(idsForCounters);
				} else {
					const updatedTreeCounters = await handleUpdateTreeWithWorker(treeCounters, idsForCounters);

					setTreeCounters(updatedTreeCounters);
					setInitCountersData(updatedTreeCounters);
					setTreeCountersIds([]);
				}
			} finally {
				setIsLoadingSetPreset(false);
			}
		} else {
			resetKpi();
		}

		setIsDirtyPresetForm(false);
	}, [
		setTreeCounters,
		setTreeKpi,
		dynamicDatesOptions,
		setInitKpiData,
		setInitCountersData,
		handleUpdateTreeWithWorker,
		setIsLoadingSetPreset,
		availableCountersIds,
		availableKpiIds,
		setTreeKpiIds,
		setTreeCountersIds,
		treeKpi,
		treeCounters,
		minDate,
		resetField,
		resetKpi,
		resetSitesAndVendor,
		formEditablePreset,
		setDefaultDates,
		setIsDirtyPresetForm,
		setValue,
	]);

	return {
		setSelectedPreset,
	};
};
